import type { OrganizationPointDto } from "common/models/api/models/OrganizationPointDto";
import type { PointsFindByRequest } from "common/models/point/PointsFindByRequest";
import { UseQueryResult, useQuery } from "react-query";
import useUserState from "ui/adapter/state/useUserState";
import usePaymentGatewayOrganizationAddress from "ui/payment-gateway/hooks/usePaymentGatewayOrganization";
import { Queries } from "ui/query/queries";
import type { UseCustomQueryOptions } from "ui/query/react-query-overrides";
import { pointsApi } from "../api/pointsApi";

/**
 * organizationAddress is always the resource of the selected role.
 * Without a selected role (public payment gateway) it falls back to the param or the gateway token's organization.
 */
export default function usePointFindBy<TData = OrganizationPointDto>(
    { organizationAddress: orgAddressParam }: Partial<PointsFindByRequest> = {},
    { enabled = true, ...restOptions }: UseCustomQueryOptions<OrganizationPointDto, unknown, TData, (string | undefined)[]> = {},
): UseQueryResult<TData> {
    const { selectedRole } = useUserState();
    const paymentGatewayOrgAddress = usePaymentGatewayOrganizationAddress();
    const organizationAddress = selectedRole ? selectedRole.resourceAddress : orgAddressParam || paymentGatewayOrgAddress;
    const safeOrganizationAddress = organizationAddress as string;

    return useQuery(
        [Queries.POINT_FIND_BY, organizationAddress],
        async () => {
            const res = await pointsApi.findBy(safeOrganizationAddress);
            return res.data;
        },
        {
            enabled: Boolean(organizationAddress) && enabled,
            ...restOptions,
        },
    );
}
